import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { API_ENDPOINTS } from '../../config/api';
import Contact from '../Contact';
import BookConsultation from '../BookConsultation';
import PWAInstallButton from '../../components/features/PWAInstallButton';
import loadGooglePlaces from '../../utils/loadGooglePlaces';
import { CHECKOUT_COUNTRIES, isAustraliaCountryCode } from '../../constants/checkoutCountries';
import { parseCustomerShippingAddress } from '../../utils/customerShippingAddress';
import './Profile.css';

// ── Design tokens (Rainbow Sails) ───────────────────────────────────────────
const T = {
  bg: '#f7f7fb',
  card: '#fff',
  ink: '#1f2937',
  inkMid: '#374151',
  inkLight: '#6b7280',
  border: 'rgba(17,24,39,0.08)',
  borderStrong: 'rgba(17,24,39,0.16)',
  pink: '#ec4899',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  gradient: 'linear-gradient(45deg, #ec4899, #8b5cf6, #06b6d4)',
  green: '#15803d', greenPale: '#dcfce7',
  amber: '#92400e', amberPale: '#fef3c7',
  blue: '#1d4ed8', bluePale: '#dbeafe',
  red: '#b91c1c', redPale: '#fee2e2',
  gray: '#6b7280', grayPale: '#f3f4f6',
  purplePale: '#f5f3ff',
};

// Invoice issuer details. TODO: source name/address/ABN from business config
// (resolveShopBusinessId / business by-domain) instead of hardcoding.
const INVOICE_ISSUER = {
  name: 'Rainbow Sails',
  lines: ['Australia'],
  abn: '',
};

const pill = (v) => {
  const m = {
    green: { background: T.greenPale, color: T.green },
    amber: { background: T.amberPale, color: T.amber },
    blue: { background: T.bluePale, color: T.blue },
    red: { background: T.redPale, color: T.red },
    gray: { background: T.grayPale, color: T.inkLight },
    purple: { background: T.purplePale, color: T.purple },
  };
  return {
    display: 'inline-block',
    fontSize: 10,
    padding: '3px 9px',
    borderRadius: 20,
    fontWeight: 600,
    textTransform: 'capitalize',
    ...(m[v] || m.gray),
  };
};

const card = { background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 18px', marginBottom: 16 };
const metric = { background: T.grayPale, borderRadius: 12, padding: '14px 16px' };
const grid4 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 16 };
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 16 };
const rowBase = { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0' };
const primaryBtn = { background: T.gradient, color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 13, cursor: 'pointer', fontWeight: 600 };
const ghostBtn = { background: '#fff', color: T.ink, border: `1px solid ${T.border}`, borderRadius: 10, padding: '8px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 500 };
const thumb = { width: 44, height: 44, borderRadius: 10, background: T.grayPale, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: 20 };
const avatar = (size = 36) => ({ width: size, height: size, borderRadius: '50%', background: T.gradient, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: Math.round(size * 0.36), flexShrink: 0 });

const TIMELINE_STEPS = ['Ordered', 'Packed', 'Shipped', 'Delivered'];

// ── Helpers ─────────────────────────────────────────────────────────────────
function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusVariant(s) {
  const v = String(s || '').toLowerCase();
  if (['paid', 'delivered', 'completed', 'complete', 'fulfilled'].includes(v)) return 'green';
  if (['pending', 'processing', 'unfulfilled', 'partial', 'partially_fulfilled'].includes(v)) return 'amber';
  if (['shipped', 'in_transit', 'out_for_delivery'].includes(v)) return 'blue';
  if (['cancelled', 'canceled', 'refunded', 'failed'].includes(v)) return 'red';
  return 'gray';
}

function productThumb(item) {
  const p = item?.product;
  const url = p?.primaryImage || (Array.isArray(p?.images) && p.images[0]) || null;
  if (url) {
    return (
      <img
        src={url}
        alt=""
        style={{ width: 44, height: 44, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return <div style={thumb}>📦</div>;
}

function parseShippingAddress(raw) {
  const parsed = parseCustomerShippingAddress(raw);
  if (!parsed) return { street: '', city: '', state: '', postcode: '', country: 'Australia' };
  const countryEntry = CHECKOUT_COUNTRIES.find((c) => c.code === parsed.country);
  return {
    street: parsed.street,
    city: parsed.city,
    state: parsed.state,
    postcode: parsed.postalCode,
    country: countryEntry?.label || parsed.country || 'Australia',
  };
}

const AU_STATE_NAMES = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
};

function normalizeAuState(state) {
  if (!state) return '';
  const trimmed = state.trim();
  if (trimmed.length <= 3) return AU_STATE_NAMES[trimmed.toUpperCase()] || trimmed;
  return trimmed;
}

function deriveStep(order) {
  const f = String(order?.fulfillmentStatus || '').toLowerCase();
  const p = String(order?.paymentStatus || '').toLowerCase();
  if (['delivered', 'completed', 'complete'].includes(f)) return 4;
  if (['shipped', 'in_transit', 'out_for_delivery'].includes(f)) return 3;
  if (['fulfilled', 'processing', 'packed', 'partial', 'partially_fulfilled'].includes(f)) return 2;
  if (p === 'paid') return 1;
  return 0;
}

function downloadInvoice(order, customer) {
  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const name = [customer?.firstName, customer?.lastName].filter(Boolean).join(' ') || customer?.email || 'Customer';
  const itemRows = (order.items || [])
    .map(
      (it) =>
        `<div class="row"><span>${it.name || 'Item'}${it.variant ? ` (${it.variant})` : ''} × ${it.quantity || 1}</span><span>${fmt(it.total != null ? it.total : it.price)}</span></div>`
    )
    .join('');
  const optional = (label, val, opts = {}) =>
    val != null
      ? `<div class="row"${opts.green ? ' style="color:#15803d"' : ''}><span>${label}</span><span>${opts.neg ? '-' : ''}${fmt(val)}</span></div>`
      : '';
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Tax Invoice #${order.orderNumber || order.id}</title>
<style>body{font-family:'Helvetica Neue',Arial,sans-serif;color:#1f2937;padding:60px;max-width:680px;margin:auto}
h1{font-size:28px;font-weight:300;letter-spacing:-0.5px;margin-bottom:4px}
.sub{color:#6b7280;font-size:13px;margin-bottom:40px}
.row{display:flex;justify-content:space-between;padding:10px 0;border-bottom:1px solid rgba(0,0,0,0.08);font-size:13px}
.total{display:flex;justify-content:space-between;padding:14px 0;font-weight:600;font-size:15px}
.from{margin-bottom:40px;font-size:13px;line-height:1.8;color:#374151}
.label{font-size:11px;color:#6b7280;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px}</style></head><body>
<h1>Tax Invoice</h1>
<div class="sub">Invoice #${order.orderNumber || order.id} · ${order.createdAt ? new Date(order.createdAt).toLocaleDateString() : ''}</div>
<div style="display:flex;gap:60px;margin-bottom:40px">
<div><div class="label">From</div><div class="from"><strong>${INVOICE_ISSUER.name}</strong><br>${INVOICE_ISSUER.lines.join('<br>')}${INVOICE_ISSUER.abn ? `<br>ABN ${INVOICE_ISSUER.abn}` : ''}</div></div>
<div><div class="label">Billed to</div><div class="from"><strong>${name}</strong><br>${customer?.email || ''}</div></div>
</div>
<div class="row"><span><strong>Item</strong></span><span><strong>Amount</strong></span></div>
${itemRows}
${optional('Subtotal', order.subtotal)}
${optional('Shipping', order.shipping)}
${optional('GST (included in prices)', order.tax)}
${Number(order.discount) > 0 ? optional('Discount', order.discount, { green: true, neg: true }) : ''}
<div class="total"><span>Total</span><span>${fmt(order.total)} AUD</span></div>
<div style="margin-top:40px;font-size:12px;color:#6b7280">Thank you for shopping with ${INVOICE_ISSUER.name}. This is a valid tax invoice for GST purposes.</div>
</body></html>`;
  const blob = new Blob([html], { type: 'text/html' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${INVOICE_ISSUER.name.replace(/\s+/g, '-')}-Invoice-${order.orderNumber || order.id}.html`;
  a.click();
}

// ── Reusable components ──────────────────────────────────────────────────────
function Pill({ v, children }) {
  return <span style={pill(v)}>{children}</span>;
}

function Toggle({ on, onChange }) {
  return (
    <div
      onClick={() => onChange(!on)}
      style={{ width: 38, height: 22, borderRadius: 22, background: on ? T.purple : '#d1d5db', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}
    >
      <div style={{ position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
    </div>
  );
}

function MetricCard({ icon, label, value, sub }) {
  return (
    <div style={metric}>
      <div style={{ fontSize: 11, color: T.inkLight, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{icon}</span>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: T.ink }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: T.inkLight, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function OrderTimeline({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', margin: '4px 0 4px' }}>
      {TIMELINE_STEPS.map((s, i) => {
        const done = i < step;
        const active = i === step;
        return (
          <div key={s} style={{ flex: 1, textAlign: 'center', position: 'relative' }}>
            {i < TIMELINE_STEPS.length - 1 && (
              <div style={{ position: 'absolute', top: 10, left: '50%', right: '-50%', height: 2, background: done ? T.green : '#e5e7eb', zIndex: 0 }} />
            )}
            <div
              style={{ width: 20, height: 20, borderRadius: '50%', background: done ? T.green : active ? T.purple : '#e5e7eb', margin: '0 auto 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: done || active ? '#fff' : T.inkLight, position: 'relative', zIndex: 1 }}
            >
              {done ? '✓' : i + 1}
            </div>
            <div style={{ fontSize: 10, color: done ? T.green : active ? T.purple : T.inkLight, fontWeight: done || active ? 600 : 400 }}>{s}</div>
          </div>
        );
      })}
    </div>
  );
}

const defaultBusinessComms = {
  newsletters: true,
  promotions: true,
  sales: true,
  marketing: true,
};

const ALLOWED_TABS = ['overview', 'orders', 'conversations', 'support', 'notifications', 'profile', 'settings'];

const TITLES = {
  overview: 'Overview',
  orders: 'Orders',
  conversations: 'Messages',
  support: 'Support',
  notifications: 'In-app',
  profile: 'Profile',
  settings: 'Settings',
};

const CustomerProfile = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    customer,
    isAuthenticated,
    logout,
    getAuthHeaders,
    refreshCustomer,
    getSignInWithMangoUrl,
    personalAccountId,
  } = useCustomerAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [orders, setOrders] = useState([]);
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [businessComms, setBusinessComms] = useState(defaultBusinessComms);
  const [savingComms, setSavingComms] = useState(false);
  const [commsMessage, setCommsMessage] = useState('');
  const [supportTickets, setSupportTickets] = useState([]);
  const [openTicketCount, setOpenTicketCount] = useState(0);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketLoading, setTicketLoading] = useState(false);
  const [ticketReply, setTicketReply] = useState('');
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', orderId: '' });
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [activity, setActivity] = useState([]);
  const [creditBalance, setCreditBalance] = useState(0);
  const [profileForm, setProfileForm] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [showReturnForm, setShowReturnForm] = useState(false);
  const [returnForm, setReturnForm] = useState({ reason: '', type: 'return', preferredResolution: 'refund' });
  const [returnSubmitting, setReturnSubmitting] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState(5);
  const [feedbackComment, setFeedbackComment] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const profileStreetRef = useRef(null);

  const currentBusinessKey = `${window.location.hostname}:${window.location.origin}`;
  const localCommsKey = customer?.id ? `frontend_business_comms:${customer.id}` : null;

  const handleAuthExpired = useCallback(() => {
    // Token is missing/expired/invalid — clear it and send the user to login instead of
    // leaving a "logged in" shell that silently fails every request.
    logout();
    const ret = `${window.location.pathname}${window.location.search}`;
    navigate(`/customer/login?return=${encodeURIComponent(ret)}`);
  }, [logout, navigate]);

  const loadOrders = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.orders, {
        headers: getAuthHeaders(),
      });

      if (response.status === 401) {
        handleAuthExpired();
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to load orders');
      }

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Failed to load orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, handleAuthExpired]);

  const loadConversations = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.customerProfile.conversations, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error('Failed to load conversations');
      }

      const data = await response.json();
      setConversations(data.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders]);

  const loadBusinessComms = useCallback(async () => {
    if (!localCommsKey) return;
    setCommsMessage('');

    try {
      const localRaw = localStorage.getItem(localCommsKey);
      const local = localRaw ? JSON.parse(localRaw) : {};
      const localForBusiness = local[currentBusinessKey] || defaultBusinessComms;
      setBusinessComms({ ...defaultBusinessComms, ...localForBusiness });
    } catch {
      setBusinessComms(defaultBusinessComms);
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) return;
      const data = await response.json();
      const remoteByBusiness = data?.preferences?.notificationPreferences?.businessByDomain || {};
      if (remoteByBusiness[currentBusinessKey]) {
        setBusinessComms((prev) => ({ ...prev, ...remoteByBusiness[currentBusinessKey] }));
      }
    } catch {
      // Remote sync may not be available for every token type; local fallback remains active.
    }
  }, [currentBusinessKey, getAuthHeaders, localCommsKey]);

  const loadOrderTimeline = useCallback(
    async (orderId) => {
      if (!orderId) {
        setTimelineData(null);
        return;
      }
      setTimelineLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.customerProfile.orderTimeline(orderId), {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          setTimelineData(data);
        } else {
          setTimelineData(null);
        }
      } catch (e) {
        console.error(e);
        setTimelineData(null);
      } finally {
        setTimelineLoading(false);
      }
    },
    [getAuthHeaders]
  );

  const loadNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const res = await fetch(`${API_ENDPOINTS.customerProfile.notifications}?limit=50`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.notifications || []);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setNotifLoading(false);
    }
  }, [getAuthHeaders]);

  const loadSupportTickets = useCallback(async () => {
    setTicketLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.supportTickets, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSupportTickets(data.tickets || []);
        setOpenTicketCount(data.openCount || 0);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setTicketLoading(false);
    }
  }, [getAuthHeaders]);

  const loadSelectedTicket = useCallback(
    async (ticketId) => {
      if (!ticketId) {
        setSelectedTicket(null);
        return;
      }
      setTicketLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.customerProfile.supportTicket(ticketId), { headers: getAuthHeaders() });
        if (res.ok) {
          const data = await res.json();
          setSelectedTicket(data.ticket);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setTicketLoading(false);
      }
    },
    [getAuthHeaders]
  );

  const loadActivity = useCallback(async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.customerProfile.activity}?limit=30`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setActivity(data.activity || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, [getAuthHeaders]);

  const loadCredits = useCallback(async () => {
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.credits, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setCreditBalance(Number(data.balance) || 0);
      }
    } catch (e) {
      console.error(e);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!customer) return;
    const addr = parseShippingAddress(customer.shippingAddress);
    setProfileForm({
      firstName: customer.firstName || '',
      lastName: customer.lastName || '',
      phone: customer.phone || '',
      shippingAddress: addr,
    });
  }, [customer]);

  // Google Places address autocomplete on profile shipping street (same as checkout).
  const profilePlacesAttachedRef = useRef(false);
  useEffect(() => {
    if (activeTab !== 'profile') {
      profilePlacesAttachedRef.current = false;
      return undefined;
    }
    if (profilePlacesAttachedRef.current) return undefined;

    let active = true;
    let autocomplete = null;
    let listener = null;

    const attach = (places) => {
      if (!active || !places) return;
      if (!profileStreetRef.current) {
        requestAnimationFrame(() => attach(places));
        return;
      }
      if (profilePlacesAttachedRef.current) return;

      try {
        autocomplete = new places.Autocomplete(profileStreetRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address'],
        });

        listener = autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place?.address_components) return;

          const get = (type, useShort = false) => {
            const c = place.address_components.find((x) => x.types.includes(type));
            if (!c) return '';
            return useShort ? c.short_name : c.long_name;
          };

          const streetNumber = get('street_number');
          const route = get('route');
          const street = [streetNumber, route].filter(Boolean).join(' ').trim();
          const city = get('locality') || get('postal_town') || get('sublocality') || '';
          const stateRaw = get('administrative_area_level_1');
          const postcode = get('postal_code');
          const country = get('country') || 'Australia';
          const state = isAustraliaCountryCode(country) ? normalizeAuState(stateRaw) : stateRaw;

          setProfileForm((prev) => {
            if (!prev) return prev;
            return {
              ...prev,
              shippingAddress: {
                ...prev.shippingAddress,
                street: street || prev.shippingAddress.street,
                city: city || prev.shippingAddress.city,
                state: state || prev.shippingAddress.state,
                postcode: postcode || prev.shippingAddress.postcode,
                country: country || prev.shippingAddress.country,
              },
            };
          });
        });
        profilePlacesAttachedRef.current = true;
      } catch {
        // Places unavailable — manual entry still works
      }
    };

    loadGooglePlaces().then(attach);

    return () => {
      active = false;
      if (listener && typeof listener.remove === 'function') listener.remove();
      document.querySelectorAll('.pac-container').forEach((el) => el.remove());
    };
  }, [activeTab]);

  useEffect(() => {
    const t = searchParams.get('tab');
    if (ALLOWED_TABS.includes(t)) {
      setActiveTab(t);
    }
  }, [searchParams]);

  const goTab = (tab, extra = {}) => {
    setActiveTab(tab);
    const next = { tab, ...extra };
    const ot = searchParams.get('orderTimeline');
    if (ot && tab === 'orders' && !extra.orderTimeline) next.orderTimeline = ot;
    if (tab !== 'support') delete next.ticket;
    if (tab !== 'orders') delete next.orderTimeline;
    setSearchParams(next, { replace: true });
  };

  const openSupportForOrder = (order) => {
    setNewTicket({
      subject: `Order #${order.orderNumber}`,
      description: `I need help with order #${order.orderNumber}.`,
      orderId: order.id,
    });
    goTab('support');
  };

  const createSupportTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.description.trim()) return;
    setCreatingTicket(true);
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.supportTickets, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: newTicket.subject.trim(),
          description: newTicket.description.trim(),
          orderId: newTicket.orderId || undefined,
          category: 'other',
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setNewTicket({ subject: '', description: '', orderId: '' });
        await loadSupportTickets();
        goTab('support', { ticket: data.ticket.id });
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCreatingTicket(false);
    }
  };

  const sendTicketReply = async () => {
    if (!selectedTicket || !ticketReply.trim()) return;
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.supportTicketMessages(selectedTicket.id), {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: ticketReply.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedTicket(data.ticket);
        setTicketReply('');
        loadSupportTickets();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const submitReturnRequest = async (order) => {
    if (!returnForm.reason.trim()) return;
    setReturnSubmitting(true);
    try {
      const items = (order.items || []).map((it) => ({
        orderItemId: it.id,
        name: it.name,
        quantity: it.quantity || 1,
      }));
      const res = await fetch(API_ENDPOINTS.customerProfile.returns, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderId: order.id,
          type: returnForm.type,
          reason: returnForm.reason.trim(),
          items,
          preferredResolution: returnForm.preferredResolution,
        }),
      });
      if (res.ok) {
        setShowReturnForm(false);
        setReturnForm({ reason: '', type: 'return', preferredResolution: 'refund' });
        loadActivity();
      }
    } catch (e) {
      console.error(e);
    } finally {
      setReturnSubmitting(false);
    }
  };

  const saveProfile = async () => {
    if (!profileForm) return;
    setSavingProfile(true);
    setProfileMessage('');
    try {
      const res = await fetch(API_ENDPOINTS.customerProfile.me, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: profileForm.firstName,
          lastName: profileForm.lastName,
          phone: profileForm.phone,
          shippingAddress: profileForm.shippingAddress,
        }),
      });
      if (res.ok) {
        await refreshCustomer();
        setProfileMessage('Profile saved.');
      } else {
        setProfileMessage('Could not save profile.');
      }
    } catch {
      setProfileMessage('Could not save profile.');
    } finally {
      setSavingProfile(false);
    }
  };

  const openOrder = (orderId) => {
    setActiveTab('orders');
    setSearchParams({ tab: 'orders', orderTimeline: orderId }, { replace: true });
  };

  const clearOrderTimeline = () => {
    setTimelineData(null);
    const next = new URLSearchParams(searchParams);
    next.delete('orderTimeline');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!isAuthenticated) {
      const ret = `${window.location.pathname}${window.location.search}`;
      navigate(`/customer/login?return=${encodeURIComponent(ret)}`);
      return;
    }

    // Load data based on active tab
    if (activeTab === 'orders' || activeTab === 'overview') {
      loadOrders();
    }
    if (activeTab === 'conversations') {
      loadConversations();
    }
    if (activeTab === 'settings') {
      loadBusinessComms();
    }
    if (activeTab === 'notifications') {
      loadNotifications();
    }
    if (activeTab === 'support') {
      loadSupportTickets();
      loadOrders(); // for the "which order is this about?" selector on the new-request form
    }
    if (activeTab === 'overview') {
      loadActivity();
      loadCredits();
      loadSupportTickets();
    }
  }, [isAuthenticated, activeTab, navigate, loadOrders, loadConversations, loadBusinessComms, loadNotifications, loadSupportTickets, loadActivity, loadCredits]);

  const ticketId = searchParams.get('ticket');
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'support') return;
    if (ticketId) {
      loadSelectedTicket(ticketId);
    } else {
      setSelectedTicket(null);
    }
  }, [isAuthenticated, activeTab, ticketId, loadSelectedTicket]);

  const orderTimelineId = searchParams.get('orderTimeline');
  useEffect(() => {
    if (!isAuthenticated || activeTab !== 'orders') return;
    if (orderTimelineId) {
      loadOrderTimeline(orderTimelineId);
    } else {
      setTimelineData(null);
    }
  }, [isAuthenticated, activeTab, orderTimelineId, loadOrderTimeline]);

  useEffect(() => {
    if (isAuthenticated) {
      loadNotifications();
    }
  }, [isAuthenticated, loadNotifications]);

  const handleLogout = () => {
    logout();
    navigate('/customer/login');
  };

  const saveBusinessComms = async () => {
    if (!localCommsKey) return;
    setSavingComms(true);
    setCommsMessage('');
    try {
      const localRaw = localStorage.getItem(localCommsKey);
      const local = localRaw ? JSON.parse(localRaw) : {};
      const mergedLocal = { ...local, [currentBusinessKey]: businessComms };
      localStorage.setItem(localCommsKey, JSON.stringify(mergedLocal));

      try {
        const getRes = await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
          headers: getAuthHeaders(),
        });
        const existing = getRes.ok ? await getRes.json() : {};
        const existingPrefs = existing?.preferences?.notificationPreferences || {};
        const existingByBusiness = existingPrefs.businessByDomain || {};
        await fetch(`${API_ENDPOINTS.customerProfile.me.replace('/customer-profile/me', '/customers/me/preferences')}`, {
          method: 'PUT',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            notificationPreferences: {
              ...existingPrefs,
              businessByDomain: {
                ...existingByBusiness,
                [currentBusinessKey]: businessComms,
              },
            },
          }),
        });
      } catch {
        // Local save still succeeds even if remote endpoint is unavailable.
      }

      setCommsMessage('Preferences saved for this business.');
    } catch {
      setCommsMessage('Could not save preferences right now.');
    } finally {
      setSavingComms(false);
    }
  };

  if (!customer) {
    return (
      <div className="cust-dash" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: T.inkLight }}>Loading…</p>
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.readAt).length;
  const initials = `${customer.firstName?.[0] || ''}${customer.lastName?.[0] || ''}`.toUpperCase() || 'U';
  const fullName = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || customer.email;
  const selectedOrder = orderTimelineId ? orders.find((o) => o.id === orderTimelineId) : null;

  const NAV = [
    { id: 'overview', label: 'Overview', icon: '⊞' },
    { id: 'orders', label: 'Orders', icon: '📦', badge: orders.length > 0 ? String(orders.length) : null },
    { id: 'conversations', label: 'Messages', icon: '💬' },
    { id: 'notifications', label: 'In-app', icon: '🔔', badge: unreadCount > 0 ? String(unreadCount) : null },
    { id: 'support', label: 'Support', icon: '🎧', badge: openTicketCount > 0 ? String(openTicketCount) : null },
  ];
  const NAV2 = [
    { id: 'profile', label: 'Profile', icon: '○' },
    { id: 'settings', label: 'Settings', icon: '◈' },
  ];

  // ── Page renderers ─────────────────────────────────────────────────────────
  const renderOverview = () => (
    <div>
      <div style={grid4}>
        <MetricCard icon="📦" label="Orders" value={orders.length || customer.orderCount || 0} sub="All time" />
        <MetricCard icon="🎧" label="Open requests" value={openTicketCount} sub="Support tickets" />
        <MetricCard icon="💳" label="Store credit" value={`$${creditBalance.toFixed(2)}`} sub="Available balance" />
        <MetricCard icon="🔔" label="In-app" value={unreadCount} sub={unreadCount === 1 ? '1 unread' : `${unreadCount} unread`} />
      </div>
      <div style={grid2}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Recent orders</span>
            <span style={{ fontSize: 12, color: T.purple, cursor: 'pointer', fontWeight: 600 }} onClick={() => goTab('orders')}>
              View all →
            </span>
          </div>
          {orders.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 12px', color: T.inkLight }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>🛍️</div>
              <p style={{ margin: '0 0 12px' }}>No orders yet.</p>
              <button style={primaryBtn} onClick={() => navigate('/gallery')}>
                Start shopping
              </button>
            </div>
          ) : (
            orders.slice(0, 4).map((o, i) => (
              <div
                key={o.id}
                style={{ ...rowBase, borderBottom: i < Math.min(orders.length, 4) - 1 ? `1px solid ${T.border}` : 'none', cursor: 'pointer' }}
                onClick={() => openOrder(o.id)}
              >
                <div style={thumb}>📦</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>Order #{o.orderNumber}</div>
                  <div style={{ fontSize: 11, color: T.inkLight, marginTop: 2 }}>{new Date(o.createdAt).toLocaleDateString()}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>${Number(o.total || 0).toFixed(2)}</div>
                  <div style={{ marginTop: 3 }}>
                    <Pill v={statusVariant(o.fulfillmentStatus)}>{o.fulfillmentStatus}</Pill>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        <div>
          <div style={{ ...card, background: T.purplePale, border: `1px solid ${T.purple}22` }}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Need a hand?</div>
            <div style={{ fontSize: 13, color: T.inkMid, lineHeight: 1.6, marginBottom: 12 }}>
              Plan a support call or message us about an order — we usually reply fast.
            </div>
            <button style={primaryBtn} onClick={() => goTab('support')}>
              Contact support
            </button>
          </div>
          <div style={card}>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Activity</div>
            {activity.length === 0 ? (
              <div style={{ fontSize: 13, color: T.inkLight, marginTop: 6 }}>No activity yet.</div>
            ) : (
              activity.slice(0, 5).map((a, i) => (
                <div
                  key={a.id}
                  style={{ padding: '10px 0', borderBottom: i < Math.min(activity.length, 5) - 1 ? `1px solid ${T.border}` : 'none', cursor: a.actionUrl ? 'pointer' : 'default' }}
                  onClick={() => a.actionUrl && navigate(a.actionUrl)}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: a.unread ? T.purple : T.ink }}>{a.title}</div>
                  {a.body && <div style={{ fontSize: 12, color: T.inkMid, marginTop: 2 }}>{a.body}</div>}
                  <div style={{ fontSize: 11, color: T.inkLight, marginTop: 2 }}>{formatWhen(a.at)}</div>
                </div>
              ))
            )}
          </div>
          {!personalAccountId && (
            <div style={{ ...card, marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Mango Personal</div>
              <div style={{ fontSize: 13, color: T.inkMid, lineHeight: 1.6, marginBottom: 10 }}>
                Link your Mango account for one sign-in across shops and your personal calendar.
              </div>
              <a href={getSignInWithMangoUrl()} style={{ ...primaryBtn, display: 'inline-block', textDecoration: 'none' }}>
                Sign in with Mango
              </a>
            </div>
          )}
          {personalAccountId && (
            <div style={{ ...card, marginTop: 16, background: T.greenPale, border: `1px solid ${T.green}33` }}>
              <div style={{ fontSize: 13, color: T.green, fontWeight: 600 }}>Connected to Mango Personal</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderOrderDetail = () => {
    if (timelineLoading && !selectedOrder) {
      return <p style={{ color: T.inkLight }}>Loading…</p>;
    }
    if (!selectedOrder) {
      return (
        <div>
          <button onClick={clearOrderTimeline} style={{ ...ghostBtn, marginBottom: 12 }}>
            ← Back to orders
          </button>
          <div style={card}>
            <p style={{ color: T.inkLight, margin: 0 }}>Could not load this order. It may belong to a different account.</p>
          </div>
        </div>
      );
    }
    const o = selectedOrder;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={clearOrderTimeline} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: T.inkLight }}>
            ←
          </button>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>Order #{o.orderNumber}</div>
            <div style={{ fontSize: 12, color: T.inkLight }}>{new Date(o.createdAt).toLocaleDateString()}</div>
          </div>
          <Pill v={statusVariant(o.paymentStatus)}>{o.paymentStatus}</Pill>
          <Pill v={statusVariant(o.fulfillmentStatus)}>{o.fulfillmentStatus}</Pill>
          <button onClick={() => downloadInvoice(o, customer)} style={{ ...ghostBtn, fontSize: 12, padding: '7px 12px' }}>
            ⬇ Invoice
          </button>
        </div>

        <div style={card}>
          <OrderTimeline step={deriveStep(o)} />
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Items</div>
          {(o.items || []).map((item) => (
            <div key={item.id || item.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: `1px solid ${T.border}`, fontSize: 13 }}>
              {productThumb(item)}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{item.name}</div>
                <div style={{ fontSize: 12, color: T.inkLight }}>
                  {item.quantity} × ${Number(item.price || 0).toFixed(2)} each
                </div>
              </div>
              <div style={{ fontWeight: 500 }}>${Number(item.total || 0).toFixed(2)}</div>
              {item.productId && (
                <button type="button" style={{ ...ghostBtn, fontSize: 11, padding: '6px 10px' }} onClick={() => navigate(`/product/${item.productId}`)}>
                  Buy again
                </button>
              )}
            </div>
          ))}
          {(o.subtotal != null || o.tax != null || o.shipping != null) && (
            <div style={{ marginTop: 10, paddingTop: 10, fontSize: 13, color: T.inkMid }}>
              {o.subtotal != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span>Subtotal</span>
                  <span>${Number(o.subtotal).toFixed(2)}</span>
                </div>
              )}
              {o.shipping != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span>Shipping</span>
                  <span>${Number(o.shipping).toFixed(2)}</span>
                </div>
              )}
              {o.tax != null && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                  <span>GST (included in prices)</span>
                  <span>${Number(o.tax).toFixed(2)}</span>
                </div>
              )}
              {Number(o.discount) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', color: T.green }}>
                  <span>Discount</span>
                  <span>-${Number(o.discount).toFixed(2)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 0', marginTop: 6, borderTop: `1px solid ${T.border}`, fontWeight: 700, fontSize: 14 }}>
                <span>Total</span>
                <span>${Number(o.total || 0).toFixed(2)} AUD</span>
              </div>
            </div>
          )}
          <p style={{ fontSize: 11, color: T.inkLight, marginTop: 10, marginBottom: 0 }}>
            Amounts are GST-inclusive. The GST line is the portion of your subtotal that is tax (for reporting), not an extra charge on top.
          </p>
        </div>

        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Progress</div>
          {timelineLoading ? (
            <p style={{ color: T.inkLight, margin: 0 }}>Loading…</p>
          ) : timelineData ? (
            <div>
              <ol style={{ margin: '0 0 6px', paddingLeft: 18, fontSize: 13 }}>
                {(timelineData.events || []).map((ev, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <span style={{ color: T.inkLight }}>{formatWhen(ev.at)}</span> — {ev.label}
                  </li>
                ))}
              </ol>
              {timelineData.fulfillmentPolicy?.lineProgress?.length > 0 && (
                <div style={{ fontSize: 13, marginTop: 8 }}>
                  <strong>Each item</strong>
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {timelineData.fulfillmentPolicy.lineProgress.map((l, j) => (
                      <li key={j}>
                        {l.name}: {l.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: T.inkLight, margin: 0 }}>Could not load progress for this order.</p>
          )}
          {o.printfulOrderId && <p style={{ fontSize: 12, color: T.inkLight, marginTop: 8, marginBottom: 0 }}>Printful: {o.printfulOrderId}</p>}
          {o.trackingNumber && (
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(o.trackingNumber + ' tracking')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...primaryBtn, display: 'inline-block', marginTop: 10, textDecoration: 'none', fontSize: 12 }}
            >
              Track shipment
            </a>
          )}
        </div>

        {['delivered', 'completed', 'complete'].includes(String(o.fulfillmentStatus || '').toLowerCase()) && !feedbackSent && (
          <div style={card}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>How was this order?</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setFeedbackRating(n)}
                  style={{ ...ghostBtn, background: feedbackRating >= n ? T.amberPale : '#fff', padding: '6px 10px' }}
                >
                  {n}★
                </button>
              ))}
            </div>
            <textarea
              value={feedbackComment}
              onChange={(e) => setFeedbackComment(e.target.value)}
              placeholder="Optional comment"
              rows={2}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, fontSize: 13 }}
            />
            <button
              type="button"
              style={primaryBtn}
              onClick={async () => {
                try {
                  await fetch(API_ENDPOINTS.orderFeedback, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ orderId: o.id, rating: feedbackRating, comment: feedbackComment.trim() || undefined }),
                  });
                  setFeedbackSent(true);
                } catch (e) {
                  console.error(e);
                }
              }}
            >
              Submit feedback
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <button style={primaryBtn} type="button" onClick={() => openSupportForOrder(o)}>
            Contact support about this order
          </button>
          <button style={ghostBtn} type="button" onClick={() => setShowReturnForm((v) => !v)}>
            Request return / cancellation
          </button>
        </div>

        {showReturnForm && (
          <div style={{ ...card, marginTop: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Return or cancellation</div>
            <select
              value={returnForm.type}
              onChange={(e) => setReturnForm((p) => ({ ...p, type: e.target.value }))}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8 }}
            >
              <option value="return">Return</option>
              <option value="cancellation">Cancellation</option>
            </select>
            <textarea
              value={returnForm.reason}
              onChange={(e) => setReturnForm((p) => ({ ...p, reason: e.target.value }))}
              placeholder="Tell us why"
              rows={3}
              style={{ width: '100%', padding: 8, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, fontSize: 13 }}
            />
            <button type="button" style={primaryBtn} disabled={returnSubmitting} onClick={() => submitReturnRequest(o)}>
              {returnSubmitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderOrdersList = () => (
    <div>
      <p style={{ fontSize: 13, color: T.inkLight, margin: '0 0 12px' }}>
        Amounts are GST-inclusive. Open an order to see progress and download a tax invoice.
      </p>
      {isLoading ? (
        <p style={{ color: T.inkLight }}>Loading orders…</p>
      ) : orders.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 16px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🛍️</div>
          <h3 style={{ margin: '0 0 8px' }}>No orders yet</h3>
          <p style={{ color: T.inkLight, marginTop: 0 }}>Start your journey with {INVOICE_ISSUER.name}.</p>
          <button style={primaryBtn} onClick={() => navigate('/gallery')}>
            Start shopping
          </button>
        </div>
      ) : (
        <div style={card}>
          {orders.map((o, i) => (
            <div key={o.id} style={{ ...rowBase, borderBottom: i < orders.length - 1 ? `1px solid ${T.border}` : 'none', flexWrap: 'wrap' }}>
              <div style={thumb}>📦</div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>Order #{o.orderNumber}</div>
                <div style={{ fontSize: 11, color: T.inkLight, marginTop: 2 }}>
                  {new Date(o.createdAt).toLocaleDateString()} · {o.items?.length || 0} item{(o.items?.length || 0) === 1 ? '' : 's'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>${Number(o.total || 0).toFixed(2)}</div>
                <div style={{ marginTop: 3, display: 'flex', gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                  <Pill v={statusVariant(o.paymentStatus)}>{o.paymentStatus}</Pill>
                  <Pill v={statusVariant(o.fulfillmentStatus)}>{o.fulfillmentStatus}</Pill>
                </div>
              </div>
              <button onClick={() => openOrder(o.id)} style={{ ...ghostBtn, fontSize: 12, padding: '7px 12px', flexShrink: 0 }}>
                View →
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderConversations = () => (
    <div>
      {isLoading ? (
        <p style={{ color: T.inkLight }}>Loading conversations…</p>
      ) : conversations.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 16px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🌈</div>
          <p style={{ color: T.inkLight, margin: 0 }}>No conversations yet. Start chatting with Rainbow!</p>
        </div>
      ) : (
        conversations.map((conv) => (
          <div key={conv.id} style={card} id={`conv-${conv.id}`}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${T.border}` }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>Conversation</span>
              <span style={{ fontSize: 12, color: T.inkLight }}>
                {new Date(conv.startedAt).toLocaleDateString()} – {new Date(conv.lastMessageAt).toLocaleDateString()}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {conv.messages.map((msg) => {
                const mine = msg.role === 'user';
                return (
                  <div key={msg.id}>
                    {!mine && <div style={{ fontSize: 10, color: T.inkLight, marginBottom: 3 }}>Rainbow 🌈</div>}
                    <div
                      style={{
                        maxWidth: '78%',
                        alignSelf: mine ? 'flex-end' : 'flex-start',
                        marginLeft: mine ? 'auto' : 0,
                        background: mine ? T.gradient : T.grayPale,
                        color: mine ? '#fff' : T.ink,
                        borderRadius: mine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
                        padding: '9px 13px',
                        fontSize: 13,
                        lineHeight: 1.5,
                      }}
                    >
                      {msg.content.split('\n').map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < msg.content.split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </div>
                    {msg.navigationUrl && (
                      <div style={{ fontSize: 11, color: T.inkLight, fontStyle: 'italic', marginTop: 3, textAlign: mine ? 'right' : 'left' }}>
                        Navigated to: {msg.navigationUrl}
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: T.inkLight, marginTop: 3, textAlign: mine ? 'right' : 'left' }}>{new Date(msg.timestamp).toLocaleString()}</div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                style={ghostBtn}
                onClick={async () => {
                  const lastUser = [...conv.messages].reverse().find((m) => m.role === 'user');
                  setNewTicket({
                    subject: 'Help from chat',
                    description: lastUser?.content || 'I need help from my chat conversation.',
                    orderId: '',
                  });
                  const res = await fetch(API_ENDPOINTS.customerProfile.supportTickets, {
                    method: 'POST',
                    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      subject: 'Help from chat',
                      description: lastUser?.content || 'I need help from my chat conversation.',
                      conversationId: conv.id,
                      category: 'other',
                    }),
                  });
                  if (res.ok) {
                    const data = await res.json();
                    goTab('support', { ticket: data.ticket.id });
                  }
                }}
              >
                Escalate to support
              </button>
            </div>
          </div>
        ))
      )}
      <div style={{ ...card, marginTop: 16 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Message Rainbow</div>
        <textarea
          value={chatInput}
          onChange={(e) => setChatInput(e.target.value)}
          placeholder="Ask a question…"
          rows={3}
          style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13, marginBottom: 8 }}
        />
        <button
          type="button"
          style={primaryBtn}
          disabled={chatSending || !chatInput.trim()}
          onClick={async () => {
            setChatSending(true);
            try {
              await fetch(API_ENDPOINTS.chatbot.chat, {
                method: 'POST',
                headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: chatInput.trim() }),
              });
              setChatInput('');
              loadConversations();
            } catch (e) {
              console.error(e);
            } finally {
              setChatSending(false);
            }
          }}
        >
          {chatSending ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );

  const renderNotifications = () => (
    <div>
      <p style={{ color: T.inkLight, fontSize: 13, marginTop: 0 }}>Order updates and links, delivered in-app.</p>
      {unreadCount > 0 && (
        <button
          type="button"
          onClick={async () => {
            try {
              await fetch(API_ENDPOINTS.customerProfile.notificationsReadAll, {
                method: 'POST',
                headers: getAuthHeaders(),
              });
              loadNotifications();
            } catch (e) {
              console.error(e);
            }
          }}
          style={{ ...ghostBtn, marginBottom: 12 }}
        >
          Mark all read
        </button>
      )}
      {notifLoading ? (
        <p style={{ color: T.inkLight }}>Loading…</p>
      ) : notifications.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '40px 16px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔔</div>
          <p style={{ color: T.inkLight, margin: 0 }}>No in-app messages yet.</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {notifications.map((n) => (
            <div key={n.id} style={{ ...card, marginBottom: 0, background: n.readAt ? '#fff' : T.purplePale, border: `1px solid ${n.readAt ? T.border : T.purple + '33'}` }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>{n.title}</div>
              <div style={{ fontSize: 13, color: T.inkMid, marginTop: 4 }}>{n.body}</div>
              <div style={{ fontSize: 12, color: T.inkLight, marginTop: 6 }}>{formatWhen(n.createdAt)}</div>
              <div style={{ marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                {n.actionUrl && (
                  <button
                    type="button"
                    style={{ color: T.purple, fontSize: 13, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', fontWeight: 600 }}
                    onClick={async () => {
                      if (!n.readAt) {
                        try {
                          await fetch(API_ENDPOINTS.customerProfile.markNotificationRead(n.id), {
                            method: 'PATCH',
                            headers: getAuthHeaders(),
                          });
                          loadNotifications();
                        } catch (err) {
                          console.error(err);
                        }
                      }
                      navigate(n.actionUrl);
                    }}
                  >
                    Open
                  </button>
                )}
                {!n.readAt && (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', color: T.inkLight, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}
                    onClick={async () => {
                      try {
                        await fetch(API_ENDPOINTS.customerProfile.markNotificationRead(n.id), {
                          method: 'PATCH',
                          headers: getAuthHeaders(),
                        });
                        loadNotifications();
                      } catch (e) {
                        console.error(e);
                      }
                    }}
                  >
                    Mark read
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderSupport = () => (
    <div>
      <p style={{ color: T.inkLight, fontSize: 14, marginTop: 0, maxWidth: '42rem' }}>
        Open a support ticket — we reply in your account. Book a consultation below when you want a live call.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>Your tickets</div>
          {ticketLoading && supportTickets.length === 0 ? (
            <p style={{ color: T.inkLight, fontSize: 13 }}>Loading…</p>
          ) : supportTickets.length === 0 ? (
            <p style={{ color: T.inkLight, fontSize: 13 }}>No tickets yet.</p>
          ) : (
            supportTickets.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => goTab('support', { ticket: t.id })}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 0',
                  border: 'none',
                  borderBottom: `1px solid ${T.border}`,
                  background: 'transparent',
                  cursor: 'pointer',
                  fontWeight: selectedTicket?.id === t.id ? 700 : 500,
                  color: t.unreadForCustomer ? T.purple : T.ink,
                }}
              >
                <div style={{ fontSize: 13 }}>{t.subject}</div>
                <div style={{ fontSize: 11, color: T.inkLight }}>{t.ticketNumber} · {t.status}</div>
              </button>
            ))
          )}
        </div>

        <div>
          {selectedTicket ? (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{selectedTicket.subject}</div>
              <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 12 }}>
                {selectedTicket.ticketNumber} · {selectedTicket.status}
                {selectedTicket.order && ` · Order #${selectedTicket.order.orderNumber}`}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                {(selectedTicket.messages || []).map((m) => (
                  <div
                    key={m.id}
                    style={{
                      alignSelf: m.role === 'customer' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      background: m.role === 'customer' ? T.gradient : T.grayPale,
                      color: m.role === 'customer' ? '#fff' : T.ink,
                      padding: '9px 12px',
                      borderRadius: 10,
                      fontSize: 13,
                    }}
                  >
                    <div style={{ fontSize: 10, opacity: 0.8, marginBottom: 4 }}>
                      {m.role === 'staff' ? m.staffName || 'Support' : 'You'} · {formatWhen(m.createdAt)}
                    </div>
                    {m.body}
                  </div>
                ))}
              </div>
              {selectedTicket.status !== 'closed' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <textarea
                    value={ticketReply}
                    onChange={(e) => setTicketReply(e.target.value)}
                    placeholder="Reply…"
                    rows={2}
                    style={{ flex: 1, padding: 8, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13 }}
                  />
                  <button type="button" style={primaryBtn} onClick={sendTicketReply}>
                    Send
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div style={card}>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 10 }}>New request</div>
              <div style={{ fontSize: 11, color: T.inkLight, marginBottom: 4 }}>Which order is this about? (optional)</div>
              <select
                value={newTicket.orderId}
                onChange={(e) => {
                  const orderId = e.target.value;
                  const order = orders.find((o) => o.id === orderId);
                  setNewTicket((p) => ({
                    ...p,
                    orderId,
                    // Prefill the subject from the order unless the customer already wrote their own
                    subject:
                      order && (!p.subject.trim() || /^Order #/.test(p.subject))
                        ? `Order #${order.orderNumber}`
                        : p.subject,
                  }));
                }}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, fontSize: 13, background: '#fff' }}
              >
                <option value="">General question — no specific order</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>
                    {`Order #${o.orderNumber}`}
                    {o.createdAt ? ` · ${new Date(o.createdAt).toLocaleDateString()}` : ''}
                    {o.total != null ? ` · $${Number(o.total).toFixed(2)}` : ''}
                  </option>
                ))}
              </select>
              <input
                value={newTicket.subject}
                onChange={(e) => setNewTicket((p) => ({ ...p, subject: e.target.value }))}
                placeholder="Subject"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, fontSize: 13 }}
              />
              <textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket((p) => ({ ...p, description: e.target.value }))}
                placeholder="Describe your issue"
                rows={4}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, marginBottom: 8, fontSize: 13 }}
              />
              <button type="button" style={primaryBtn} disabled={creatingTicket} onClick={createSupportTicket}>
                {creatingTicket ? 'Creating…' : 'Open ticket'}
              </button>
            </div>
          )}
        </div>
      </div>

      <div style={{ marginTop: 24 }}>
        <Contact embedded />
      </div>
      <div id="customer-support-booking" style={{ marginTop: 24 }}>
        <BookConsultation embedded />
      </div>
    </div>
  );

  const renderProfile = () => {
    if (!profileForm) return <p style={{ color: T.inkLight }}>Loading…</p>;
    const addr = profileForm.shippingAddress;
    const field = (label, key, opts = {}) => (
      <label key={key} style={{ display: 'block', marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: T.inkLight, marginBottom: 4 }}>{label}</div>
        <input
          value={opts.nested ? addr[key] : profileForm[key]}
          onChange={(e) => {
            if (opts.nested) {
              setProfileForm((p) => ({ ...p, shippingAddress: { ...p.shippingAddress, [key]: e.target.value } }));
            } else {
              setProfileForm((p) => ({ ...p, [key]: e.target.value }));
            }
          }}
          style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13 }}
        />
      </label>
    );
    return (
      <div style={grid2}>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Personal details</div>
          {field('First name', 'firstName')}
          {field('Last name', 'lastName')}
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: T.inkLight, marginBottom: 4 }}>Email</div>
            <div style={{ fontSize: 13, fontWeight: 500, padding: '10px 0' }}>{customer.email}</div>
          </label>
          {field('Phone', 'phone')}
          <div style={{ fontSize: 12, color: T.inkLight, marginBottom: 12 }}>
            Loyalty {(customer.loyaltyPoints || 0).toLocaleString()} · Spent ${Number(customer.totalSpent || 0).toFixed(2)}
          </div>
          <button type="button" style={primaryBtn} disabled={savingProfile} onClick={saveProfile}>
            {savingProfile ? 'Saving…' : 'Save profile'}
          </button>
          {profileMessage && <div style={{ fontSize: 13, color: T.green, marginTop: 8 }}>{profileMessage}</div>}
        </div>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Shipping address</div>
          <label style={{ display: 'block', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: T.inkLight, marginBottom: 4 }}>Street</div>
            <input
              ref={profileStreetRef}
              value={addr.street}
              onChange={(e) =>
                setProfileForm((p) => ({
                  ...p,
                  shippingAddress: { ...p.shippingAddress, street: e.target.value },
                }))
              }
              placeholder="Start typing your address…"
              autoComplete="street-address"
              style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 13 }}
            />
          </label>
          {field('City', 'city', { nested: true })}
          {field('State', 'state', { nested: true })}
          {field('Postcode', 'postcode', { nested: true })}
          {field('Country', 'country', { nested: true })}
        </div>
      </div>
    );
  };

  const renderSettings = () => {
    const items = [
      ['newsletters', 'Newsletters', 'Stories, drops and updates'],
      ['promotions', 'Promotions', 'Discount codes and offers'],
      ['sales', 'Sales alerts', 'Seasonal and flash sales'],
      ['marketing', 'Marketing content', 'Curated picks and campaigns'],
    ];
    return (
      <div>
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>Business communication preferences</div>
          <div style={{ fontSize: 13, color: T.inkLight, marginBottom: 8 }}>Choose what you want to receive from {INVOICE_ISSUER.name}.</div>
          {items.map(([k, label, sub], i) => (
            <div key={k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: i < items.length - 1 ? `1px solid ${T.border}` : 'none' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 11, color: T.inkLight, marginTop: 2 }}>{sub}</div>
              </div>
              <Toggle
                on={Boolean(businessComms[k])}
                onChange={(v) => {
                  setBusinessComms((prev) => ({ ...prev, [k]: v }));
                  setCommsMessage('');
                }}
              />
            </div>
          ))}
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button onClick={saveBusinessComms} disabled={savingComms} style={{ ...primaryBtn, opacity: savingComms ? 0.6 : 1, cursor: savingComms ? 'not-allowed' : 'pointer' }}>
              {savingComms ? 'Saving…' : 'Save preferences'}
            </button>
            {commsMessage && <span style={{ fontSize: 13, color: T.green }}>{commsMessage}</span>}
          </div>
        </div>
        <div style={{ ...card, background: T.bluePale, border: `1px solid ${T.blue}22` }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 6 }}>Install the app</div>
          <div style={{ fontSize: 13, color: T.inkMid, lineHeight: 1.6, marginBottom: 12 }}>
            Add this site to your home screen for quick access to orders and support — no App Store needed.
          </div>
          <PWAInstallButton />
        </div>
      </div>
    );
  };

  const renderPage = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'orders':
        return orderTimelineId ? renderOrderDetail() : renderOrdersList();
      case 'conversations':
        return renderConversations();
      case 'notifications':
        return renderNotifications();
      case 'support':
        return renderSupport();
      case 'profile':
        return renderProfile();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const renderNavItem = (n) => (
    <button key={n.id} className={`cust-dash__navitem${activeTab === n.id ? ' is-active' : ''}`} onClick={() => goTab(n.id)} type="button">
      <span style={{ fontSize: 15, width: 18, textAlign: 'center' }}>{n.icon}</span>
      <span>{n.label}</span>
      {n.badge && <span className="badge">{n.badge}</span>}
    </button>
  );

  return (
    <div className="cust-dash">
      <div className="cust-dash__inner">
        <aside className="cust-dash__sidebar">
          <div className="cust-dash__brand">
            <div style={{ fontSize: 16, fontWeight: 700, background: T.gradient, WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>{INVOICE_ISSUER.name}</div>
            <div style={{ fontWeight: 400, color: T.inkLight, fontSize: 12, marginTop: 2 }}>Customer portal</div>
          </div>
          <nav className="cust-dash__nav">
            {NAV.map(renderNavItem)}
            <div className="cust-dash__seclabel">Account</div>
            {NAV2.map(renderNavItem)}
          </nav>
          <div className="cust-dash__userfoot">
            <div style={avatar(36)}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName}</div>
              <div style={{ fontSize: 11, color: T.inkLight }}>{customer.customerType || 'Member'}</div>
            </div>
          </div>
        </aside>

        <main className="cust-dash__main">
          <div className="cust-dash__topbar">
            <div style={{ fontSize: 16, fontWeight: 700 }}>{TITLES[activeTab] || 'Account'}</div>
            <button onClick={handleLogout} style={ghostBtn} type="button">
              Log out
            </button>
          </div>
          <div className="cust-dash__content">{renderPage()}</div>
        </main>
      </div>
    </div>
  );
};

export default CustomerProfile;
